import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

// Bauhaus palette — coral, mustard, bone outlines on ink black.
const CORAL = "#E04F2A";
const MUSTARD = "#E8B948";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, -180]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100vh] w-full overflow-hidden bg-ink"
    >
      {/* Bauhaus composition */}
      <BauhausComposition progress={scrollYProgress} />

      {/* Soft vignette so foreground text never fights with shapes */}
      <div className="absolute inset-0 z-20 bg-gradient-to-b from-transparent via-transparent to-ink pointer-events-none" />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-40 flex items-center justify-between px-6 md:px-12 py-6">
        <div className="flex items-center gap-3" data-testid="brand-mark">
          <span className="block h-2 w-2 rounded-full bg-bone" />
          <span className="font-manrope text-xs uppercase tracking-[0.3em] text-bone/80">
            Samuel Ortiz
          </span>
        </div>
        <div className="hidden md:flex items-center gap-8 font-manrope text-xs uppercase tracking-[0.25em] text-bone/70">
          <a href="#about" data-testid="nav-about">About</a>
          <a href="#projects" data-testid="nav-projects">Projects</a>
          <a href="#contact" data-testid="nav-contact">Contact</a>
        </div>
        <a
          href="https://www.behance.net/samoso"
          target="_blank"
          rel="noreferrer"
          data-testid="nav-behance"
          className="font-manrope text-xs uppercase tracking-[0.25em] text-bone/80 border border-bone/30 px-4 py-2 hover:bg-bone hover:text-ink transition-colors"
        >
          Behance ↗
        </a>
      </div>

      {/* Hero text (foreground) */}
      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-30 flex h-full flex-col justify-end px-6 md:px-12 pb-24 md:pb-28"
      >
        <div className="flex items-baseline gap-4 mb-6">
          <span className="font-manrope text-xs uppercase tracking-[0.3em] text-bone/70 inline-flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#E04F2A]" />
            01 · Portfolio 2025
          </span>
          <span className="h-px flex-1 max-w-32 bg-bone/40" />
          <span className="font-manrope text-xs uppercase tracking-[0.3em] text-bone/70 hidden md:block">
            Mexico City, MX
          </span>
        </div>

        <h1
          data-testid="hero-title"
          className="font-oswald uppercase tracking-tighter leading-[0.85] text-bone text-[18vw] md:text-[14vw] lg:text-[12vw]"
        >
          Samuel
          <br />
          <span className="italic font-cormorant font-normal text-[14vw] md:text-[10vw] lg:text-[9vw] tracking-normal">
            Ortiz
          </span>
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <p className="font-cormorant italic text-2xl md:text-3xl text-bone/90 max-w-md leading-snug">
            Creative Art Director — Motion Graphics & Visual Direction.
          </p>
          <motion.a
            href="#projects"
            data-testid="hero-cta"
            className="flex items-center gap-3 font-manrope text-xs uppercase tracking-[0.3em] text-bone/80 group"
            whileHover={{ x: 4 }}
          >
            <span>See the work</span>
            <ArrowDown className="h-4 w-4 group-hover:translate-y-1 transition-transform" />
          </motion.a>
        </div>
      </motion.div>

      {/* Bottom stats */}
      <div className="absolute bottom-6 left-6 md:left-12 z-30 hidden md:flex items-center gap-4 font-manrope text-[10px] uppercase tracking-[0.25em] text-bone/60">
        <span>Multidisciplinary</span>
        <span className="h-px w-8 bg-bone/30" />
        <span>10 years of creative direction</span>
      </div>
    </section>
  );
}

function BauhausComposition({ progress }) {
  // Different parallax speeds for visual depth
  const yCircle = useTransform(progress, [0, 1], [0, -160]);
  const ySquare = useTransform(progress, [0, 1], [0, 90]);
  const yRing = useTransform(progress, [0, 1], [0, 60]);
  const yRect = useTransform(progress, [0, 1], [0, -80]);
  const yBar = useTransform(progress, [0, 1], [0, -120]);
  const yTri = useTransform(progress, [0, 1], [0, 40]);

  const fade = (delay) => ({
    initial: { opacity: 0, scale: 0.85 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1], delay },
  });

  return (
    <div className="absolute inset-0 z-10 pointer-events-none">
      {/* Big coral circle, top-right */}
      <motion.div
        {...fade(0.15)}
        style={{ y: yCircle, backgroundColor: CORAL }}
        className="absolute right-[8%] top-[12%] h-[34vw] w-[34vw] md:h-[26vw] md:w-[26vw] rounded-full"
        aria-hidden
      />

      {/* Hollow bone ring, center-left */}
      <motion.div
        {...fade(0.3)}
        style={{ y: yRing }}
        className="absolute left-[18%] top-[24%] h-[18vw] w-[18vw] md:h-[14vw] md:w-[14vw] rounded-full border-2 border-bone/60"
        aria-hidden
      />

      {/* Mustard square, bottom-center-right */}
      <motion.div
        {...fade(0.45)}
        style={{ y: ySquare, backgroundColor: MUSTARD }}
        className="absolute right-[32%] bottom-[18%] h-[10vw] w-[10vw] md:h-[7vw] md:w-[7vw]"
        aria-hidden
      />

      {/* Thin vertical bone bar, mid-right */}
      <motion.div
        {...fade(0.6)}
        style={{ y: yBar }}
        className="absolute right-[22%] top-[8%] w-[3px] h-[42vh] bg-bone/70 origin-top"
        aria-hidden
      />

      {/* Tall mustard rectangle, far left — sits over the name, leaves the subtitle clear */}
      <motion.div
        {...fade(0.55)}
        style={{ y: yRect, backgroundColor: MUSTARD }}
        className="absolute left-[6%] md:left-[7%] bottom-[34%] md:bottom-[38%] w-[4vw] md:w-[3vw] h-[22vh] md:h-[28vh] opacity-90"
        aria-hidden
      />

      {/* Triangle (built via clip-path), top-center */}
      <motion.div
        {...fade(0.7)}
        style={{
          y: yTri,
          clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
          backgroundColor: CORAL,
        }}
        className="absolute left-[40%] top-[6%] h-[10vw] w-[12vw] md:h-[7vw] md:w-[9vw] opacity-90"
        aria-hidden
      />

      {/* Small bone hollow circle, bottom-left */}
      <motion.div
        {...fade(0.8)}
        className="absolute left-[8%] bottom-[28%] h-[5vw] w-[5vw] md:h-[3.5vw] md:w-[3.5vw] rounded-full border border-bone/50"
        aria-hidden
      />

      {/* Diagonal coral line, mid */}
      <motion.div
        {...fade(0.9)}
        className="absolute top-[58%] left-[35%] h-[2px] w-[22vw] origin-left"
        style={{ transform: "rotate(-18deg)", backgroundColor: CORAL }}
        aria-hidden
      />

      {/* Horizontal bone bar, bottom-right */}
      <motion.div
        {...fade(1)}
        className="absolute right-[6%] bottom-[36%] h-[2px] w-[14vw] bg-bone/60"
        aria-hidden
      />

      {/* Small filled mustard dot, top-left */}
      <motion.div
        {...fade(1.1)}
        className="absolute left-[36%] top-[18%] h-3 w-3 md:h-4 md:w-4 rounded-full"
        style={{ backgroundColor: MUSTARD }}
        aria-hidden
      />
    </div>
  );
}
